// src/sectors/pilates/sessions/modals/SessionCreateModal.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useSxTokens } from './ui/sxTokens';

import { staffApi } from 'sectors/pilates/staff/api/staffApi';

import { SessionKindModal } from './kinds/SessionKindModal';
import { SessionCreatePackageModal } from './kinds/SessionCreatePackageModal';
import { SessionCreatePaidModal } from './kinds/SessionCreatePaidModal';
import { SessionCreateFreeModal } from './kinds/SessionCreateFreeModal';
import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';

export function SessionCreateModal({
  open,
  onClose,
  onCreated,
  dayISO,
  startMin,
  defaultTrainerId = 'ALL',
  initialDraft,
}) {
  const theme = useMuiTheme();
  const tokens = useSxTokens(theme);

  const [trainers, setTrainers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);

  useEffect(() => {
    if (!open) return;

    let alive = true;
    setStaffLoading(true);
    setStaffError(null);

    staffApi
      .trainerPicker()
      .then((items) => {
        if (!alive) return;

        const mapped = (Array.isArray(items) ? items : [])
          .filter((x) => x && x.id != null)
          .map((x) => ({
            id: x.id,
            name: String(x.fullName || '—').trim() || '—',
          }));

        setTrainers(mapped);
      })
      .catch((e) => {
        if (!alive) return;
        console.error('[SessionCreateModal] trainerPicker failed', e);
        setTrainers([]);
        setStaffError(e);
      })
      .finally(() => {
        if (!alive) return;
        setStaffLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open]);

  // ✅ IMPORTANT: stable function + axios res.data unwrap
  const loadMemberPackages = useCallback(async (memberId) => {
    const res = await membersApi.memberPackages(memberId);
    const data = res?.data ?? res;

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  }, []);

  // flow
  const [kindOpen, setKindOpen] = useState(false);
  const [kind, setKind] = useState(null); // "PACKAGE" | "PAID" | "FREE"

  const [pkgOpen, setPkgOpen] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [freeOpen, setFreeOpen] = useState(false);

  useEffect(() => {
    if (open) {
      const bm = String(initialDraft?.billingMode || '').toUpperCase();
      if (initialDraft) {
        // edit/clone gibi: direkt ilgili modal
        setKind(null);
        setKindOpen(false);
        setPkgOpen(bm !== 'PAID' && bm !== 'FREE');
        setPaidOpen(bm === 'PAID');
        setFreeOpen(bm === 'FREE');
      } else {
        // normal create: kind seçtir
        setKind(null);
        setKindOpen(true);
        setPkgOpen(false);
        setPaidOpen(false);
        setFreeOpen(false);
      }
    } else {
      setKindOpen(false);
      setPkgOpen(false);
      setPaidOpen(false);
      setFreeOpen(false);
      setKind(null);
    }
  }, [open, initialDraft]);

  const closeAll = () => {
    setKindOpen(false);
    setPkgOpen(false);
    setPaidOpen(false);
    setFreeOpen(false);
    setKind(null);
    onClose?.();
  };

  const backToKind = () => {
    setPkgOpen(false);
    setPaidOpen(false);
    setFreeOpen(false);
    setKindOpen(true);
  };

  const pickKind = (k) => {
    setKind(k);
    setKindOpen(false);
    if (k === 'PACKAGE') setPkgOpen(true);
    if (k === 'PAID') setPaidOpen(true);
    if (k === 'FREE') setFreeOpen(true);
  };
  const handleValidate = useCallback(
    async (payload) => {
      const t = (trainers || []).find((x) => String(x.id) === String(payload?.trainerId));
      const trainerName = String(payload?.trainerName || t?.name || '').trim();

      const fixed = { ...payload, trainerName };

      const res = await sessionsApi.validateCreateSession(fixed);
      return res?.data ?? res;
    },
    [trainers]
  );

  const handleCreate = useCallback(
    async (payload) => {
      const t = (trainers || []).find((x) => String(x.id) === String(payload?.trainerId));
      const trainerName = String(payload?.trainerName || t?.name || '').trim();

      const fixed = { ...payload, trainerName };

      console.log('[SessionCreateModal] FINAL payload =>', fixed);

      const res = await sessionsApi.createSession(fixed);
      const data = res?.data ?? res;
      return data;
    },
    [trainers]
  );

  const common = {
    onClose: closeAll,
    onBack: backToKind,
    onValidate: handleValidate, // ✅ EKLE
    onCreate: handleCreate, // ✅ burası kritik
    dayISO,
    startMin,
    defaultTrainerId,
    tokens,
    initialDraft,
    trainers,
    staffLoading,
    staffError,
  };

  return (
    <>
      <SessionKindModal open={kindOpen} onClose={closeAll} onPick={pickKind} tokens={tokens} />
      <SessionCreatePackageModal
        open={pkgOpen}
        {...common}
        onValidate={handleValidate}
        loadMemberPackages={loadMemberPackages}
        onCreated={async () => {
          await onCreated?.(); // ✅ parent’ta refetch
        }}
      />
      <SessionCreatePaidModal
        open={paidOpen}
        {...common}
        onValidate={sessionsApi.validatePaidSession}
        onCreated={async () => {
          await onCreated?.();
        }}
      />{' '}
      <SessionCreateFreeModal
        open={freeOpen}
        {...common}
        onValidate={sessionsApi.validateFreeSession}
        onCreated={async () => {
          await onCreated?.(); // parent refetch
        }}
      />
    </>
  );
}
